import { QRCodeSVG } from "qrcode.react";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Reveal, SectionLabel } from "../components/Motion";
import { useApp } from "../AppContext";
import { C, IMAGES } from "../content";
import { Train, Bus, Plane, MapPin, Building2, Flag, Footprints, ExternalLink } from "lucide-react";

const LANDMARK_ICONS = {
  niwas: ({ size = 26 }) => (
    <div className="relative inline-flex">
      <Building2 size={size} className="text-saffron-600" />
      <Flag size={14} className="text-saffron-600 fill-saffron-500 absolute -top-1 -right-2" />
    </div>
  ),
  mandir: ({ size = 26 }) => <Footprints size={size} className="text-deepred-700" />,
};

export default function Reach() {
  const { lang } = useApp();
  const L = (o) => o[lang] ?? o.en;
  const mapsUrl = (coords, name) => `https://www.google.com/maps/search/?api=1&query=${coords}&query_place_id=${encodeURIComponent(name)}`;
  const niwas = C.reach.landmarks[0];
  return (
    <div>
      <Seo title="How to Reach Audumbar" description="Reach Shri Datta Kshetra Audumbar by rail (Kirloskarwadi ~15km, Bhilawadi ~8km), road (Ashta ~12km — nearest major bus stop, then local buses/autos) or air (Kolhapur ~58km). Google Map + QR code + nearby temples." />
      <PageBanner label={lang === "mr" ? "प्रवास" : "Directions"} titleObj={C.reach} image={IMAGES.ghatWalkway} />
      <section className="py-16 lg:py-24 max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-3 gap-6">
          <Reveal>
            <div className="rounded-2xl border border-earth-900/10 bg-white p-7 h-full" data-testid="reach-rail">
              <div className="w-12 h-12 rounded-xl bg-saffron-500/10 text-saffron-600 flex items-center justify-center mb-5"><Train size={24} /></div>
              <h3 className="font-serif-head text-2xl text-earth-900 mb-3">{lang === "mr" ? "रेल्वेने" : "By Rail"}</h3>
              <ul className="space-y-2 text-earth-800/80">{C.reach.rail.map((r, i) => <li key={i}>• {L(r)}</li>)}</ul>
              <div className="mt-5 rounded-xl bg-saffron-500/10 border border-saffron-500/25 p-4 text-earth-800/90 text-sm leading-relaxed" data-testid="rail-pickup-note">
                <b className="text-deepred-700">{lang === "mr" ? "पिक-अप सेवा:" : "Pick-up service:"}</b> {lang === "mr" ? C.reach.rail_note_mr : C.reach.rail_note_en}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-2xl border border-earth-900/10 bg-white p-7 h-full" data-testid="reach-road">
              <div className="w-12 h-12 rounded-xl bg-saffron-500/10 text-saffron-600 flex items-center justify-center mb-5"><Bus size={24} /></div>
              <h3 className="font-serif-head text-2xl text-earth-900 mb-3">{lang === "mr" ? "रस्त्याने" : "By Road"}</h3>
              <div className="inline-flex items-center gap-2 rounded-full bg-deepred-700/10 text-deepred-700 px-3 py-1 text-xs font-semibold mb-3">
                {lang === "mr" ? "जवळचे बसस्थानक: आष्टा (~१२ कि.मी.)" : "Nearest bus stop: Ashta (~12 km)"}
              </div>
              <p className="text-earth-800/80">{lang === "mr" ? C.reach.road_mr : C.reach.road_en}</p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="rounded-2xl border border-earth-900/10 bg-white p-7 h-full" data-testid="reach-air">
              <div className="w-12 h-12 rounded-xl bg-saffron-500/10 text-saffron-600 flex items-center justify-center mb-5"><Plane size={24} /></div>
              <h3 className="font-serif-head text-2xl text-earth-900 mb-3">{lang === "mr" ? "विमानाने" : "By Air"}</h3>
              <p className="text-earth-800/80">{lang === "mr" ? C.reach.air_mr : C.reach.air_en}</p>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-14">
          <SectionLabel>{lang === "mr" ? "नकाशा" : "Location Map"}</SectionLabel>
          <p className="text-earth-700 mb-4">{lang === "mr" ? "भक्त निवासाचे अचूक स्थान खाली दर्शविले आहे. मोबाईलवर सहज मार्गदर्शनासाठी उजवीकडील QR कोड स्कॅन करा." : "Exact location of the Bhakt Niwas is shown below. Scan the QR code on the right for instant navigation on your mobile."}</p>
          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            <div className="rounded-3xl overflow-hidden border border-earth-900/10 shadow-[0_20px_50px_rgba(59,34,19,0.1)]" data-testid="google-map">
              <iframe title="Audumbar Bhakt Niwas Map" width="100%" height="440" style={{ border: 0 }} loading="lazy" allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${niwas.coords}(${encodeURIComponent(L({ mr: niwas.name_mr, en: niwas.name_en }))})&z=17&output=embed`} />
            </div>
            <div className="rounded-3xl border border-earth-900/10 bg-white p-6 flex flex-col items-center text-center" data-testid="map-qr">
              <div className="text-xs uppercase tracking-wider text-earth-600 mb-3">{lang === "mr" ? "QR कोड स्कॅन करा" : "Scan to open in Maps"}</div>
              <div className="p-3 rounded-xl bg-white border border-earth-900/10">
                <QRCodeSVG value={`https://www.google.com/maps?q=${niwas.coords}`} size={200} level="M" bgColor="#ffffff" fgColor="#3B2213" />
              </div>
              <div className="mt-4 text-earth-800 font-serif-head text-lg leading-snug">{L({ mr: niwas.name_mr, en: niwas.name_en })}</div>
              <div className="mt-1 text-xs text-earth-600">{niwas.coords}</div>
              <a href={`https://www.google.com/maps?q=${niwas.coords}`} target="_blank" rel="noreferrer" data-testid="open-in-maps"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-4 py-2 text-sm font-semibold">
                <ExternalLink size={14} /> {lang === "mr" ? "मॅप्समध्ये उघडा" : "Open in Maps"}
              </a>
            </div>
          </div>

          {/* Landmarks legend */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="map-landmarks">
            {C.reach.landmarks.map((lm) => {
              const Icon = LANDMARK_ICONS[lm.key];
              return (
                <a key={lm.key} href={`https://www.google.com/maps?q=${lm.coords}`} target="_blank" rel="noreferrer"
                  data-testid={`landmark-${lm.key}`}
                  className="group flex items-center gap-4 rounded-2xl border border-earth-900/10 bg-sand-100 px-5 py-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(59,34,19,0.12)]">
                  <div className="w-12 h-12 rounded-xl bg-white border border-earth-900/10 flex items-center justify-center shrink-0">
                    <Icon size={26} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-head text-lg text-earth-900 truncate">{L({ mr: lm.name_mr, en: lm.name_en })}</div>
                    <div className="text-xs text-earth-600">{lm.coords}</div>
                  </div>
                  <ExternalLink size={16} className="text-saffron-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              );
            })}
          </div>
        </Reveal>

        <Reveal className="mt-14">
          <SectionLabel>{lang === "mr" ? "जवळील स्थळे" : "Nearby Places"}</SectionLabel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {C.reach.nearby.map((n, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl bg-sand-100 border border-earth-900/10 px-5 py-4 text-earth-800">
                <MapPin size={18} className="text-deepred-700 shrink-0" />{L(n)}
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
